import { bunja } from 'bunja';
import { atom } from 'jotai';

import { Timestamp } from '@flipster/apis/types';
import type { TriggerPriceType } from '@flipster/apis/v2/trade/types';
import { getEffectiveIsolatedMax, getModeMaxLeverage } from '@flipster/features/src/shared/leverage/isolated-cap';
import { i18nBunja } from '@flipster/i18n';
import { calcAmountToMargin } from '@flipster/utils/calculator/order-preview';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import { dateToEpochString } from '@flipster/utils/datetime';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';

import type { AdvancedTpSlPreset } from '../advanced-tpsl';
import { isValidTriggerReferencePrice, marginTypeBunja, oneWayPositionLeverageBunja, symbolBunja } from './base';
import { bracketBunja } from './bracket';
import { OrderState, formBunja, type TriggerExecutionType } from './form';
import { getLeverageError } from './logic/validation/leverage';
import { realtimeStateBunja } from './price';

export type Side = 'Long' | 'Short' | 'unknown';

export type OrderformPayload = {
  side: Side;
  priceType: 'market' | 'trigger' | 'limit';
  triggerType: 'price' | 'time';
  triggerPrice?: string;
  triggerPriceType: TriggerPriceType;
  triggerReferencePrice: string;
  triggerTimestamp?: Timestamp;
  /** Trigger 발동 후 체결 방식 (priceType==='trigger'일 때만 의미 있음). */
  triggerExecutionType: TriggerExecutionType;
  orderState: OrderState;
  amount?: string;
  tpSl: {
    tp?: string;
    sl?: string;
    enabled: boolean;
    tpTriggerPriceType?: TriggerPriceType;
    slTriggerPriceType?: TriggerPriceType;
  };
  /** Advanced TP/SL preset (Limit/BBO 실행 방식). null = 미설정 → 인라인 간편 TP/SL(tpSl) 사용. */
  advancedTpSl: AdvancedTpSlPreset | null;
  reduceOnly: boolean;
  referencePrice: string;
  limitPrice?: string;
  isBbo: boolean;
};

export interface ValidationResult {
  triggerPrice?: { type: 'toast'; message: string };
  triggerTimestamp?: { type: 'field'; message: string };
  leverage?: { type: 'field'; message: string };
  quantity?: { type: 'field'; message: string; action?: { label: string; onClick: () => void } };
  amount?: { type: 'field'; message: string; action?: { label: string; onClick: () => void } };
  margin?: { type: 'field'; message: string; action?: { label: string; onClick: () => void } };
  blocked?: { type: 'toast'; message: string };
}

export const validationBunja = bunja(() => {
  const { getT } = bunja.use(i18nBunja);
  const { symbolInfoAtom, symbol } = bunja.use(symbolBunja);
  const form = bunja.use(formBunja);
  const { marginTypeAtom } = bunja.use(marginTypeBunja);
  const { amountAtom } = bunja.use(realtimeStateBunja);
  const { bracketValidationAtom } = bunja.use(bracketBunja);
  const { hasOneWayPositionAtom, positionLeverageAtom } = bunja.use(oneWayPositionLeverageBunja);

  const t = getT();

  const triggerPriceErrorMessageAtom = atom((get) => {
    const priceType = get(form.priceTypeAtom);
    const triggerType = get(form.triggerTypeAtom);
    const triggerPrice = get(form.triggerPriceAtom);
    if (priceType !== 'trigger' || triggerType !== 'price') return;
    if (Number(triggerPrice || 0) === 0) return t('Trade.r250401.Trigger_price_zero_error_toast');
    const triggerReferencePrice = get(form.triggerReferencePriceAtom);
    if (!isValidTriggerReferencePrice(triggerReferencePrice)) {
      return t('Common.r250401.Unexpected_error_message_multiline');
    }
    // Trigger Limit + non-BBO: limit price도 0이면 동일 에러
    const triggerExecutionType = get(form.triggerExecutionTypeAtom);
    if (triggerExecutionType === 'limit' && !get(form.isBboAtom)) {
      const limitPrice = get(form.limitPriceAtom);
      if (Number(limitPrice || 0) === 0) return t('Trade.r250401.Trigger_price_zero_error_toast');
    }
  });

  const triggerTimestampErrorMessageAtom = atom((get) => {
    const priceType = get(form.priceTypeAtom);
    const triggerType = get(form.triggerTypeAtom);
    const triggerTimestamp = get(form.triggerTimestampAtom);
    if (priceType !== 'trigger' || triggerType !== 'time') return;
    if (Num(triggerTimestamp || 0).isLessThanOrEqualTo(dateToEpochString(new Date()))) {
      return t('Trade.r250401.Trigger_time_validation_error');
    }
  });

  const leverageErrorMessageAtom = atom((get) => {
    const s = get(form.orderStateAtom);
    const { maxLeverageHardLimit, maxInitLeverageIsolatedMargin, notionalMaxOrderAmount } = get(symbolInfoAtom);
    const effectiveIsolatedMax = getEffectiveIsolatedMax(maxLeverageHardLimit, maxInitLeverageIsolatedMargin);
    const maxLeverage = getModeMaxLeverage(get(marginTypeAtom), maxLeverageHardLimit, effectiveIsolatedMax);

    // 판정은 순수 로직(getLeverageError)에 위임하고, 여기선 에러 종류 → i18n 메시지 매핑만 한다.
    const error = getLeverageError({
      leverage: s.leverage,
      kind: s.kind,
      margin: s.kind === 'margin' ? s.margin : undefined,
      maxLeverage,
      notionalMaxOrderAmount,
      hasOneWayPosition: get(hasOneWayPositionAtom),
      positionLeverage: get(positionLeverageAtom),
      // 반대 방향 감소 주문도 reduce-only처럼 취급해 leverage 상한을 스킵 (submit payload는 명시값 유지).
      reduceOnly: get(form.effectiveReduceOnlyAtom),
    });

    if (!error) return;

    switch (error.type) {
      case 'minInput':
        return { message: t('Trade.r250401.Min_leverage_input_error') };
      case 'exceedsMaxLeverage':
        return { message: t('Trade.r250401.Smaller_leverage_error', { leverage: `${error.maxLeverage}X` }) };
      case 'belowPositionLeverage':
        return { message: t('Position_leverage.r250401.Decrease_leverage_not_allowed_error') };
      case 'exceedsNotionalMax':
        return { message: t('Trade.r250401.Smaller_leverage_or_margin_error', { leverage: `${error.maxLeverage}X` }) };
    }
  });

  const quantityErrorMessageAtom = atom((get) => {
    const s = get(form.orderStateAtom);
    if (s.kind !== 'quantity') return;
    if (s.quantity === '') return;

    const quantity = Num(s.quantity || '0');
    // minimum order quantity validation
    const calcPrice = get(form.calculationPriceAtom);
    const { positionUnit, notionalMinOrderAmount, notionalMaxOrderAmount, tradeQtyPrecision } = get(symbolInfoAtom);
    const minQty = Num(Num(notionalMinOrderAmount).dividedBy(calcPrice).toFixed(tradeQtyPrecision, BigNumber.ROUND_UP));

    if (minQty.isGreaterThan(quantity)) {
      // qty 아래 소수점보다 무조건 커야하므로 올림
      return {
        message: t('Trade.r250401.Min_quantity_error', { amount: numberFormatter(minQty), symbol: positionUnit }),
      };
    }
    // maximum order quantity validation — skipped in reduce-only mode (server validates instead).
    const reduceOnly = get(form.effectiveReduceOnlyAtom);
    if (!reduceOnly) {
      const maxQty = Num(
        Num(notionalMaxOrderAmount).dividedBy(calcPrice).toFixed(tradeQtyPrecision, BigNumber.ROUND_DOWN),
      );
      if (maxQty.isLessThan(quantity)) {
        return {
          message: t('Trade.r250401.Exceeds_quantity_submit_error', {
            amount: numberFormatter(maxQty),
            symbol: positionUnit,
          }),
        };
      }
    }

    const shouldShowError = get(form.shouldShowBracketErrorAtom);
    const { isValid, maxAvailableQty } = get(bracketValidationAtom);
    if (shouldShowError && !isValid) {
      return {
        message: maxAvailableQty.gte(minQty)
          ? `${t('Trade.r260113.Exceeds_max_position_size_error')} ${t(
              'Trade.r260128.Reduce_position_size_amount_symbol_error',
              {
                amountSymbol: `${numberFormatter(maxAvailableQty, {
                  precision: tradeQtyPrecision,
                  toFixed: true,
                })} ${positionUnit}`,
              },
            )}`
          : t('Trade.r260113.Exceeds_max_position_size_error'),
        action: {
          label: t('Common.r250401.Learn_more'),
          onClick: () => {
            window.open(`/support/trading-rules/leverage-margin/${symbol}`, '_blank');
          },
        },
      };
    }
  });

  const minAmountAtom = atom((get) => {
    const { quoteCurrency } = get(symbolInfoAtom);
    const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);
    const { notionalMinOrderAmount, unitOrderQty } = get(symbolInfoAtom);
    const calcPrice = get(form.calculationPriceAtom);

    return Math.max(
      Number(notionalMinOrderAmount),
      Number(Num(unitOrderQty).multipliedBy(calcPrice).toFixed(quoteCurrencyPrecision, BigNumber.ROUND_UP)),
    );
  });

  const baseAmountErrorMessageAtom = atom((get) => {
    const amount = Num(get(amountAtom) || '0');
    const { quoteCurrency } = get(symbolInfoAtom);
    const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);

    // minimum order amount validation
    const realMinAmount = get(minAmountAtom);
    if (amount.isLessThan(realMinAmount)) {
      return t('Trade.r250829.Min_order_size_with_leverage_error', {
        amountQuoteCurrency: `${numberFormatter(realMinAmount, { precision: quoteCurrencyPrecision, toFixed: true })} ${quoteCurrency}`,
      });
    }
  });

  const amountErrorMessageAtom = atom((get) => {
    const s = get(form.orderStateAtom);
    if (s.kind !== 'amount') return;
    if (s.amount === '') return;

    const base = get(baseAmountErrorMessageAtom);
    if (base) return { message: base };
    // maximum order amount validation — skipped in reduce-only mode (server validates instead).
    const { quoteCurrency, notionalMaxOrderAmount } = get(symbolInfoAtom);
    const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);
    const amount = Num(get(amountAtom) || '0');
    const reduceOnly = get(form.effectiveReduceOnlyAtom);

    if (!reduceOnly && amount.isGreaterThan(notionalMaxOrderAmount)) {
      return {
        message: t('Trade.r250827.Exceeds_position_size_submit_error', {
          amount: numberFormatter(notionalMaxOrderAmount, { precision: quoteCurrencyPrecision, toFixed: true }),
          quoteCurrency,
        }),
      };
    }

    const realMinAmount = get(minAmountAtom);
    const shouldShowError = get(form.shouldShowBracketErrorAtom);
    const { isValid, maxAvailablePositionSize } = get(bracketValidationAtom);
    if (shouldShowError && !isValid) {
      return {
        message: maxAvailablePositionSize.gte(realMinAmount)
          ? `${t('Trade.r260113.Exceeds_max_position_size_error')} ${t(
              'Trade.r260128.Reduce_position_size_amount_symbol_error',
              {
                amountSymbol: `${numberFormatter(maxAvailablePositionSize, {
                  precision: quoteCurrencyPrecision,
                  toFixed: true,
                })} ${quoteCurrency}`,
              },
            )}`
          : t('Trade.r260113.Exceeds_max_position_size_error'),
        action: {
          label: t('Common.r250401.Learn_more'),
          onClick: () => {
            window.open(`/support/trading-rules/leverage-margin/${symbol}`, '_blank');
          },
        },
      };
    }
  });

  const marginErrorMessageAtom = atom((get) => {
    const s = get(form.orderStateAtom);
    if (s.kind !== 'margin') return;
    if (s.margin === '') return;

    const base = get(baseAmountErrorMessageAtom);
    if (base) return { message: base };
    // maximum order amount validation — skipped in reduce-only mode (server validates instead).
    const { quoteCurrency, notionalMaxOrderAmount } = get(symbolInfoAtom);
    const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);
    const amount = Num(get(amountAtom) || '0');
    const reduceOnly = get(form.effectiveReduceOnlyAtom);
    if (!reduceOnly && amount.isGreaterThan(notionalMaxOrderAmount)) {
      return {
        message: t('Trade.r250827.Exceeds_margin_or_leverage_submit_error', {
          amount: numberFormatter(calcAmountToMargin(notionalMaxOrderAmount, s.leverage), {
            precision: quoteCurrencyPrecision,
            toFixed: true,
          }),
          quoteCurrency,
        }),
      };
    }

    const realMinAmount = get(minAmountAtom);
    const shouldShowError = get(form.shouldShowBracketErrorAtom);
    const { isValid, maxAvailableMargin } = get(bracketValidationAtom);
    if (shouldShowError && !isValid) {
      return {
        message: maxAvailableMargin.gte(realMinAmount)
          ? `${t('Trade.r260113.Exceeds_max_position_size_error')} ${t(
              'Trade.r260113.Reduce_margin_amount_symbol_error',
              {
                amountSymbol: `${numberFormatter(maxAvailableMargin, {
                  precision: quoteCurrencyPrecision,
                  toFixed: true,
                })} ${quoteCurrency}`,
              },
            )}`
          : t('Trade.r260113.Exceeds_max_position_size_error'),
        action: {
          label: t('Common.r250401.Learn_more'),
          onClick: () => {
            window.open(`/support/trading-rules/leverage-margin/${symbol}`, '_blank');
          },
        },
      };
    }
  });

  const blockedUserErrorMessageAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (
      (symbolInfo?.notionalMaxPosition && Num(symbolInfo.notionalMaxPosition).isZero()) ||
      (symbolInfo?.notionalMaxOrderAmount && Num(symbolInfo.notionalMaxOrderAmount).isZero())
    ) {
      return t('Trade.r250821.Trading_pair_not_available_error_message');
    }
  });

  const payloadAtom = atom((get): OrderformPayload => {
    return {
      side: get(form.sideAtom),
      priceType: get(form.priceTypeAtom),
      triggerType: get(form.triggerTypeAtom),
      triggerPrice: get(form.triggerPriceAtom),
      triggerPriceType: get(form.triggerPriceTypeAtom),
      triggerReferencePrice: get(form.triggerReferencePriceAtom),
      triggerTimestamp: get(form.triggerTimestampAtom),
      triggerExecutionType: get(form.triggerExecutionTypeAtom),
      orderState: get(form.orderStateAtom),
      amount: get(amountAtom),
      referencePrice: get(form.referencePriceAtom),
      tpSl: get(form.tpSlAtom),
      advancedTpSl: get(form.advancedTpSlPresetAtom),
      // 서버로 가는 reduceOnly는 반드시 명시 플래그(reduceOnlyAtom)만 쓴다 — effectiveReduceOnlyAtom(반대 방향
      // 감소 주문을 reduce-only로 취급하는 클라 검증용) 금지. 반대 방향 주문도 서버엔 일반 주문(false)으로 보낸다.
      reduceOnly: get(form.reduceOnlyAtom),
      limitPrice: get(form.limitPriceAtom),
      isBbo: get(form.isBboAtom),
    };
  });
  const validationErrorsAtom = atom((get): ValidationResult => {
    const orderTypeFormFieldError = get(form.orderTypeFormFieldErrorAtom);
    const hasDirty = Object.values(get(form.dirtyAtom)).some(Boolean);
    if (!hasDirty && !orderTypeFormFieldError) return {};

    const result: ValidationResult = {};
    if (hasDirty) {
      const triggerPriceErrorMessage = get(triggerPriceErrorMessageAtom);
      const blockedUserErrorMessage = get(blockedUserErrorMessageAtom);
      const triggerTimestampErrorMessage = get(triggerTimestampErrorMessageAtom);
      const leverageErrorMessage = get(leverageErrorMessageAtom);
      const quantityErrorMessage = get(quantityErrorMessageAtom);
      const amountErrorMessage = get(amountErrorMessageAtom);
      const marginErrorMessage = get(marginErrorMessageAtom);
      if (triggerPriceErrorMessage) result.triggerPrice = { type: 'toast', message: triggerPriceErrorMessage };
      if (triggerTimestampErrorMessage)
        result.triggerTimestamp = { type: 'field', message: triggerTimestampErrorMessage };
      if (leverageErrorMessage) result.leverage = { type: 'field', message: leverageErrorMessage.message };
      if (quantityErrorMessage)
        result.quantity = {
          type: 'field',
          message: quantityErrorMessage.message,
          action: quantityErrorMessage?.action,
        };
      if (amountErrorMessage)
        result.amount = { type: 'field', message: amountErrorMessage.message, action: amountErrorMessage?.action };
      if (marginErrorMessage)
        result.margin = { type: 'field', message: marginErrorMessage.message, action: marginErrorMessage?.action };
      if (blockedUserErrorMessage) result.blocked = { type: 'toast', message: blockedUserErrorMessage };
    }
    if (orderTypeFormFieldError) {
      result[orderTypeFormFieldError.field] = { type: 'field', message: orderTypeFormFieldError.message };
    }
    return result;
  });
  const fieldValidationErrorsAtom = atom((get): ValidationResult => {
    const orderTypeFormFieldError = get(form.orderTypeFormFieldErrorAtom);
    const result = { ...get(validationErrorsAtom) };
    const dirty = get(form.dirtyAtom);
    if (!dirty.leverage) delete result.leverage;
    if (!dirty.margin && orderTypeFormFieldError?.field !== 'margin') delete result.margin;
    if (!dirty.quantity && orderTypeFormFieldError?.field !== 'quantity') delete result.quantity;
    if (!dirty.amount && orderTypeFormFieldError?.field !== 'amount') delete result.amount;
    if (!dirty.triggerTimestamp) delete result.triggerTimestamp;
    if (!dirty.triggerPrice) delete result.triggerPrice;
    return Object.fromEntries(Object.entries(result).filter(([, { type }]) => type === 'field'));
  });

  return {
    payloadAtom,
    validationErrorsAtom,
    fieldValidationErrorsAtom,
  };
});
validationBunja.debugLabel = 'validationBunja';
